/**
 * <Modal />
 *
 * Minimal accessible modal with subtle glassmorphism backdrop:
 *   * Click-away + Escape to close.
 *   * Body scroll locked while open.
 *   * Focuses the first focusable element on open (close button fallback).
 *   * Renders into a portal so parent `overflow-hidden` never clips it.
 *
 * Kept intentionally small -- no animations library, just CSS transitions.
 */
import * as React from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Accessible title shown at top of the modal. */
  title: React.ReactNode;
  /** Optional short explainer under the title. */
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Max-width tailwind token. Defaults to `max-w-lg`. */
  sizeClass?: string;
  testid?: string;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  sizeClass = "max-w-lg",
  testid = "modal",
}: Props) {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  // Esc to close + body scroll lock.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the dialog so screen readers start there.
    requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      data-testid={testid}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      {/* Glass backdrop */}
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        data-testid={`${testid}-backdrop`}
        className="absolute inset-0 bg-ink/60 backdrop-blur-md"
      />

      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          "relative w-full rounded-3xl border border-white/20 bg-white shadow-card-lg",
          "animate-[fadeInUp_.2s_ease-out]",
          sizeClass,
        )}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-6">
          <div className="min-w-0">
            <h2 className="text-xl md:text-2xl font-black tracking-tight text-ink">
              {title}
            </h2>
            {description && (
              <p className="text-ink-muted text-sm mt-1">{description}</p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border-2 border-surface-border text-ink-muted hover:border-ink hover:text-ink transition-colors"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>
        <div className="px-6 pt-4 pb-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
